import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LayoutDashboard,
  Home,
  Key,
  User,
  Calculator,
  Wrench,
  Printer,
  Shield,
  UserCircle,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu as MenuIcon
} from "lucide-react";
import { useSidebar } from "../context/SidebarContext";

type SubMenuItem = {
  name: string;
  path: string;
};

type NavItem = {
  name: string;
  icon?: React.ReactNode;
  path?: string;
  isTitle?: boolean;
  children?: SubMenuItem[];
};

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const isAdmin = useSelector((state: any) => state.authentification?.user?.isAdmin || false);

  const navItems: NavItem[] = [
    { name: "TABLEAU DE BORD", isTitle: true },
    { name: "Dashboard",   icon: <LayoutDashboard className="h-5 w-5" />, path: "/Dashone" },
    { name: "Locations",   icon: <Key className="h-5 w-5" />,             path: "/Rent/RentHome" },
    { name: "Biens",       icon: <Home className="h-5 w-5" />,            path: "/Local/LocalHome" },
    { name: "Locataires",  icon: <User className="h-5 w-5" />,            path: "/Tenant/TenantHome" },
    { name: "Finance",     icon: <Calculator className="h-5 w-5" />,      path: "/Finance/FinanceHome" },
    { name: "Maintenance", icon: <Wrench className="h-5 w-5" />,          path: "/Work/WorkHome" },

    { name: "--------------", isTitle: true },

    {
      name: "Impressions",
      icon: <Printer className="h-5 w-5" />,
      children: [
        { name: "Imprimer un état",   path: "/Etat/Impression" },
        { name: "Liste des courriers", path: "/Etat/Courrier" },
        { name: "Envoi de courriers",  path: "/Etat/EnvoiCourrier" },
      ]
    },

    { name: "Mon espace",    icon: <UserCircle className="h-5 w-5" />, path: "/MonEspace" },
    ...(isAdmin ? [{ name: "Administrateur", icon: <Shield className="h-5 w-5" />, path: "/Administrateur" }] : []),

    { name: "DECONNEXION", icon: <LogOut className="h-5 w-5" />, path: "/logout" },
  ];
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  const toggleMenu = (menuName: string) => {
    setOpenMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuName)) {
        newSet.delete(menuName);
      } else {
        newSet.add(menuName);
      }
      return newSet;
    });
  };

  // Ouvrir automatiquement le menu parent si un enfant est actif
  useEffect(() => {
    navItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => isActive(child.path));
        if (hasActiveChild) {
          setOpenMenus(prev => new Set(prev).add(item.name));
        }
      }
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const showText = isExpanded || isHovered || isMobileOpen;

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${isExpanded || isMobileOpen ? "w-[290px]" : isHovered ? "w-[290px]" : "w-[90px]"}
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo */}
      <div className={`py-8 flex ${!showText ? "lg:justify-center" : "justify-start"}`}>
        <Link to="/">
          <img src="/images/logo.png" alt="Logo" style={{ height: 70, width: 'auto', objectFit: 'contain' }} />
        </Link>
      </div>
      
      {/* Navigation */}
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <ul className="flex flex-col gap-1">
            {navItems.map((nav, index) => (
              <li key={index}>
                {nav.isTitle ? (
                  // Titre de section
                  <h3 className={`px-3 py-2 text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold ${
                    !showText ? "lg:text-center" : ""
                  }`}>
                    {showText ? nav.name : <MenuIcon className="h-4 w-4 mx-auto" />}
                  </h3>
                ) : nav.children ? (
                  // Menu avec sous-menus
                  <div>
                    <button
                      onClick={() => toggleMenu(nav.name)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                        !showText ? "lg:justify-center" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {nav.icon}
                        {showText && <span className="text-sm font-medium">{nav.name}</span>}
                      </div>
                      {showText && (
                        openMenus.has(nav.name) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )
                      )}
                    </button>
                    
                    {/* Sous-menus */}
                    {showText && openMenus.has(nav.name) && (
                      <ul className="ml-8 mt-1 flex flex-col gap-1">
                        {nav.children.map((child, childIndex) => (
                          <li key={childIndex}>
                            <Link
                              to={child.path}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                                isActive(child.path)
                                  ? "bg-brand-500 text-white"
                                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                              }`}
                            >
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  // Menu simple
                  <Link
                    to={nav.path || "#"}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive(nav.path || "")
                        ? "bg-brand-500 text-white"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    } ${!showText ? "lg:justify-center" : ""}`}
                  >
                    {nav.icon}
                    {showText && <span className="text-sm font-medium">{nav.name}</span>}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
        <div className="px-4 pb-3 pt-2">
          <span className="text-[10px] text-gray-700 dark:text-gray-300 font-mono select-none">
            {showText ? 'v1.0.0.1' : 'v1'}
          </span>
        </div>
      </div>
    </aside>
  );
};

export default AppSidebar;